import { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSearchedResults } from "@/redux/slices/GlobalSearch";
import { usePathname } from "next/navigation";
import {
  trackSearchBarClicked,
  trackSearched,
  trackSearchResultClicked,
  getPageType,
} from "@/utils/gtmEvents";

/**
 * Custom hook for product search functionality
 *
 * Provides a reusable search implementation with debouncing and Redux state management.
 *
 * @param {number} debounceDelay - The debounce delay in milliseconds (default: 200)
 * @param {Array} industryTopProducts - Optional industry-specific top products to show as recommendations
 * @returns {Object} Search state and handlers
 * @returns {string} inputValue - Current input value
 * @returns {boolean} isSearching - Whether search dropdown should be visible
 * @returns {function} setIsSearching - Function to control search dropdown visibility
 * @returns {Array} result - Search results from Redux store
 * @returns {function} handleInputChange - Debounced input change handler
 * @returns {Object} searchProps - Props object ready to pass to ProductSearch component
 *
 * @example
 * // For global search (home/product pages)
 * const { searchProps } = useProductSearch();
 *
 * // For industry-specific search
 * const { searchProps } = useProductSearch(200, industryData?.top_products);
 *
 * return (
 *   <FeatureImageWithBlur
 *     showSearchBar={true}
 *     searchProps={searchProps}
 *   />
 * );
 */
const useProductSearch = (debounceDelay = 200, industryTopProducts = null) => {
  const dispatch = useDispatch();
  const pathname = usePathname();
  const [inputValue, setInputValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchBarClicked, setSearchBarClicked] = useState(false);
  const lastSearchQuery = useRef("");
  const result = useSelector((state) => state?.GlobalSearch?.result);
  const globalTopProducts = useSelector((state) => state?.Product?.topProducts);

  // Get recommendations from industry-specific products or global top products from Redux
  const recommendations = industryTopProducts && Array.isArray(industryTopProducts) && industryTopProducts.length > 0
    ? industryTopProducts.slice(0, 5)
    : globalTopProducts?.slice(0, 5) || [];

  // Track SEARCHED event when results are updated
  useEffect(() => {
    if (lastSearchQuery.current && inputValue.length > 0) {
      const pageUrl = typeof window !== "undefined" ? window.location.href : "";
      const pageType = typeof window !== "undefined"
        ? getPageType(window.location.pathname)
        : "other";

      trackSearched(
        pageUrl,
        pageType,
        lastSearchQuery.current,
        result?.length || 0
      );
    }
  }, [result, inputValue]);

  // Debounce function
  const debounce = useCallback((func, delay) => {
    let timeoutId;
    return function (...args) {
      const context = this;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(function () {
        func.apply(context, args);
      }, delay);
    };
  }, []);

  // Handle search bar click
  const handleSearchBarClick = useCallback(() => {
    if (!searchBarClicked) {
      const pageUrl = typeof window !== "undefined" ? window.location.href : "";
      const pageType = typeof window !== "undefined"
        ? getPageType(window.location.pathname)
        : "other";

      trackSearchBarClicked(pageUrl, pageType);
      setSearchBarClicked(true);
    }
  }, [searchBarClicked]);

  // Handle result click
  const handleResultClick = useCallback((productName, index) => {
    trackSearchResultClicked(index + 1, productName);
  }, []);

  // Handle input change
  const handleInputChange = useCallback(
    (e) => {
      const value = e?.target?.value;
      setInputValue(value);
      if (value.length > 0) {
        setIsSearching(true);
        lastSearchQuery.current = value;
      } else {
        setIsSearching(false);
      }
      dispatch(getSearchedResults(value));
    },
    [dispatch]
  );

  // Create debounced handler
  const debouncedHandleInputChange = useCallback(
    debounce(handleInputChange, debounceDelay),
    [handleInputChange, debounceDelay, debounce]
  );

  // Return all necessary values and a convenient searchProps object
  return {
    inputValue,
    isSearching,
    setIsSearching,
    result,
    recommendations,
    handleInputChange: debouncedHandleInputChange,
    handleSearchBarClick,
    handleResultClick,
    // Convenient props object that can be spread directly into ProductSearch
    searchProps: {
      inputValue,
      isSearching,
      setIsSearching,
      data: result,
      handleInputChange: debouncedHandleInputChange,
      recommendations, // Include recommendations for ProductSearch
      onSearchBarClick: handleSearchBarClick, // GTM tracking
      onResultClick: handleResultClick, // GTM tracking
    },
  };
};

export default useProductSearch;
